import { lazy, Suspense } from 'react';
import { OAuthRedirectHandler } from '@/components/auth/OAuthRedirectHandler';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { SidebarStateProvider } from '@/contexts/SidebarContext';
import { HeroAnimationProvider } from '@/contexts/HeroAnimationContext';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import AnimatedLoadingScreen from '@/components/auth/AnimatedLoadingScreen';
import PaymentReceiptPage from '@/pages/dashboard/PaymentReceiptPage';
import { useAutoEmailTriggers } from '@/hooks/use-email-triggers';
import { useRealtimeSync } from '@/hooks/use-realtime-sync';
// QueryClient is provided in main.tsx, removed duplicate import
import { TopLoader } from '@/components/ui/top-loader';
import { AnimatePresence } from 'framer-motion';
import { QueryLoaderProvider } from '@/components/providers/QueryLoaderProvider';
import { SiteProtection } from '@/components/auth/SiteProtection';
import { ChatPopupProvider } from '@/components/messaging/FacebookStyleChat/ChatPopupManager';
import { HelmetProvider } from 'react-helmet-async';
import { TidioManager } from '@/components/layout/TidioManager';
import { NotificationPermissionBanner } from '@/components/notifications/NotificationPermissionBanner';
import { BrowserNotificationsInit } from '@/components/notifications/BrowserNotificationsInit';

// Feature flags (default to false if not set)
const CANVAS_ENABLED = import.meta.env.VITE_CANVAS_ENABLED === 'true';
const RANKER_TUNING_ENABLED = import.meta.env.VITE_ENABLE_RANKER_TUNING === 'true';

// Lazy loaded page components (replacing original static imports)
const Index = lazy(() => import('./pages/public/Index'));
const Login = lazy(() => import('./pages/auth/Login'));
const RoleSelection = lazy(() => import('./pages/auth/RoleSelection'));
const FilmmakerType = lazy(() => import('./pages/auth/FilmmakerType'));
const Signup = lazy(() => import('./pages/auth/Signup'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const JoinPriority = lazy(() => import('./pages/JoinPriority'));
const PriorityWelcome = lazy(() => import('./pages/PriorityWelcome'));
const PrioritySetPassword = lazy(() => import('./pages/PrioritySetPassword'));
const GoogleCallback = lazy(() => import('./pages/auth/GoogleCallback'));
const AppleCallback = lazy(() => import('./pages/auth/AppleCallback'));
const NotFound = lazy(() => import('./pages/public/NotFound'));
const PublicPortfolio = lazy(() => import('./pages/public/PublicPortfolio'));
const PublicPortfolioView = lazy(() => import('./pages/public/PublicPortfolioView'));
const CustomUrlPortfolio = lazy(() => import('./pages/public/CustomUrlRedirect'));
const PublicBookingPage = lazy(() => import('./pages/public/PublicBookingPage'));
const ClaimJob = lazy(() => import('./pages/public/ClaimJob'));
const ClaimProfile = lazy(() => import('./pages/public/ClaimProfile'));
const PublicTalent = lazy(() => import('./pages/public/BrowseTalent'));
const PublicCalendarPage = lazy(() => import('./pages/PublicCalendarPage'));
const Support = lazy(() => import('./pages/public/Support'));
const AppRedirect = lazy(() => import('./pages/AppRedirect'));
const JobRedirect = lazy(() => import('./pages/JobRedirect'));
const JobToOpportunitiesRedirect = lazy(() => import('./components/routing/JobToOpportunitiesRedirect'));
const FeedbackPage = lazy(() => import('./pages/feedback/Feedback'));
const ProfileSetupBasic = lazy(
  () => import('./pages/profile-setup/ProfileSetupBasic')
);
const DualAccountSetup = lazy(
  () => import('./pages/profile-setup/DualAccountSetup')
);
const ProfileSetupProfessional = lazy(
  () => import('./pages/profile-setup/ProfileSetupProfessional')
);
const ProfileSetupPortfolio = lazy(
  () => import('./pages/profile-setup/ProfileSetupPortfolio')
);
const ProfileSetupPreview = lazy(
  () => import('./pages/profile-setup/ProfileSetupPreview')
);
const VerifyIdentity = lazy(
  () => import('./pages/profile-setup/VerifyIdentity')
);
const VerifyAccount = lazy(() => import('./pages/auth/VerifyAccount'));
const ClientProfileSetupBasic = lazy(
  () => import('./pages/client-profile-setup/ClientProfileSetupBasic')
);
const ClientProfileSetupPreview = lazy(
  () => import('./pages/client-profile-setup/ClientProfileSetupPreview')
);
const ClientVerifyIdentity = lazy(
  () => import('./pages/client-profile-setup/ClientVerifyIdentity')
);
const TalentDetails = lazy(
  () => import('./pages/client-dashboard/TalentDetails')
);

// Dashboard components
const DashboardHome = lazy(() => import('./pages/dashboard/DashboardHome'));
const DashboardJobs = lazy(() => import('./pages/dashboard/DashboardJobs'));
const JobDetails = lazy(() => import('./pages/dashboard/JobDetails'));
const JobApplication = lazy(() => import('./pages/dashboard/JobApplication'));
const ApplicationSubmitted = lazy(
  () => import('./pages/dashboard/ApplicationSubmitted')
);
const DashboardApplications = lazy(
  () => import('./pages/dashboard/DashboardApplications')
);
const ApplicationDetails = lazy(
  () => import('./pages/dashboard/ApplicationDetails')
);
const ContractDetails = lazy(() => import('./pages/dashboard/ContractDetails'));
const BookingDetails = lazy(() => import('./pages/dashboard/BookingDetails'));
const BookingDetailsPage = lazy(() => import('./pages/dashboard/BookingDetailsPage'));
const DashboardBookings = lazy(() => import('./pages/dashboard/DashboardBookings'));
const DashboardMessages = lazy(
  () => import('./pages/dashboard/DashboardMessages')
);
const DashboardEarnings = lazy(
  () => import('./pages/dashboard/DashboardEarnings')
);
const DashboardAccount = lazy(
  () => import('./pages/dashboard/DashboardAccount')
);
const DashboardSubscribe = lazy(
  () => import('./pages/dashboard/DashboardSubscribe')
);
const Reviews = lazy(() => import('./pages/dashboard/Reviews'));
const PaymentReceipt = lazy(() => import('./pages/dashboard/PaymentReceipt'));

// Calendar pages
const FilmmakerCalendarPage = lazy(
  () => import('./pages/filmmaker-dashboard/FilmmakerCalendarPage')
);

// Invoice pages
const FilmmakerInvoicesPage = lazy(
  () => import('./pages/filmmaker-dashboard/FilmmakerInvoicesPage')
);
const FilmmakerInvoiceCreatePage = lazy(
  () => import('./pages/filmmaker-dashboard/FilmmakerInvoiceCreatePage')
);
const FilmmakerInvoiceDetailPage = lazy(
  () => import('./pages/filmmaker-dashboard/FilmmakerInvoiceDetailPage')
);
const ClientInvoicesPage = lazy(
  () => import('./pages/client-dashboard/ClientInvoicesPage')
);
const ClientInvoiceCreatePage = lazy(
  () => import('./pages/client-dashboard/ClientInvoiceCreatePage')
);
const ClientInvoiceDetailPage = lazy(
  () => import('./pages/client-dashboard/ClientInvoiceDetailPage')
);

// Dual dashboard pages — combined views for dual filmmaker/client users
const UnifiedInvoicesPage = lazy(
  () => import('./pages/dual-dashboard/UnifiedInvoicesPage')
);

// New consolidated filmmaker dashboard pages
const FilmmakerHomePage = lazy(
  () => import('./pages/filmmaker-dashboard/FilmmakerHomePage')
);
const FilmmakerOpportunitiesPage = lazy(
  () => import('./pages/filmmaker-dashboard/FilmmakerOpportunitiesPage')
);
const FilmmakerJobsPage = lazy(
  () => import('./pages/filmmaker-dashboard/FilmmakerJobsPage')
);
const FilmmakerBookingsPage = lazy(
  () => import('./pages/filmmaker-dashboard/FilmmakerBookingsPage')
);
const FilmmakerBusinessPage = lazy(
  () => import('./pages/filmmaker-dashboard/FilmmakerBusinessPage')
);
const FilmmakerAccountPage = lazy(
  () => import('./pages/filmmaker-dashboard/FilmmakerAccountPage')
);
const FilmmakerProfilePage = lazy(
  () => import('./pages/filmmaker-dashboard/FilmmakerProfilePage')
);
const FilmmakerMessagesPage = lazy(
  () => import('./pages/filmmaker-dashboard/FilmmakerMessagesPage')
);
const ClientCalendarPage = lazy(
  () => import('./pages/client-dashboard/ClientCalendarPage')
);
const CalendarInviteResponse = lazy(
  () => import('./pages/calendar/CalendarInviteResponse')
);
const DocumentsPage = lazy(() => import('./pages/dashboard/DocumentsPage'));


// Client dashboard components
const ClientDashboardHome = lazy(
  () => import('./pages/client-dashboard/ClientDashboardHome')
);
const ClientDashboardTalent = lazy(
  () => import('./pages/client-dashboard/ClientDashboardTalent')
);
const ClientDashboardProjects = lazy(
  () => import('./pages/client-dashboard/ClientDashboardProjects')
);
const ClientDashboardMessages = lazy(
  () => import('./pages/client-dashboard/ClientDashboardMessages')
);
const ClientDashboardExpenditure = lazy(
  () => import('./pages/client-dashboard/ClientDashboardExpenditure')
);
const ClientDashboardAccount = lazy(
  () => import('./pages/client-dashboard/ClientDashboardAccount')
);

// New consolidated client dashboard pages (5-tab layout)
const ClientMessagesPage = lazy(
  () => import('./pages/client-dashboard/ClientMessagesPage')
);
const ClientFindTalentPage = lazy(
  () => import('./pages/client-dashboard/ClientFindTalentPage')
);
const ClientHiringPage = lazy(
  () => import('./pages/client-dashboard/ClientHiringPage')
);
const ClientBusinessPage = lazy(
  () => import('./pages/client-dashboard/ClientBusinessPage')
);
const ClientBookingFromLinkPage = lazy(
  () => import('./pages/client-dashboard/ClientBookingFromLinkPage')
);
const ClientDashboardSubscribe = lazy(
  () => import('./pages/client-dashboard/ClientDashboardSubscribe')
);
const WalletDepositSuccessPage = lazy(
  () => import('./pages/client-dashboard/WalletDepositSuccessPage')
);
const WalletDepositFailurePage = lazy(
  () => import('./pages/client-dashboard/WalletDepositFailurePage')
);
const ProjectCanvasPage = lazy(
  () => import('./pages/client-dashboard/ProjectCanvasPage')
);

// Project-related pages
const CreateProjectPage = lazy(
  () => import('./pages/client-dashboard/CreateProjectPage')
);
const ProjectDetailsPage = lazy(
  () => import('./pages/client-dashboard/ProjectDetailsPage')
);
const AddJobPage = lazy(() => import('./pages/client-dashboard/AddJobPage'));
const EditProjectPage = lazy(
  () => import('./pages/client-dashboard/EditProjectPage')
);
const EditJobPage = lazy(() => import('./pages/client-dashboard/EditJobPage'));
const JobDetailsPage = lazy(
  () => import('./pages/client-dashboard/JobDetailsPage')
);
const ContractPage = lazy(
  () => import('./pages/client-dashboard/ContractPage')
);
const PaymentCheckoutPage = lazy(
  () => import('./pages/client-dashboard/PaymentCheckoutPage')
);
const PaymentSuccessPage = lazy(
  () => import('./pages/client-dashboard/PaymentSuccessPage')
);
const ClientReceiptPage = lazy(
  () => import('./pages/client-dashboard/ClientReceiptPage')
);
const ReviewPage = lazy(() => import('./pages/client-dashboard/ReviewPage'));
const DisputePage = lazy(() => import('./pages/client-dashboard/DisputePage'));
const JobApplicantProfile = lazy(
  () => import('@/pages/client-dashboard/JobApplicantProfile')
);
// Pitching Room components
const ClientPitchingRoom = lazy(
  () => import('@/pages/client-dashboard/ClientPitchingRoom')
);
const FilmmakerPitchingRoom = lazy(
  () => import('@/pages/dashboard/FilmmakerPitchingRoom')
);
const FilmmakerDashboardSubscribe = lazy(
  () => import('@/pages/filmmaker-dashboard/FilmmakerDashboardSubscribe')
);

// Legal pages
const TermsOfService = lazy(() => import('./pages/public/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/public/PrivacyPolicy'));
const DataDeletion = lazy(() => import('./pages/public/DataDeletion'));
const RefundPolicy = lazy(() => import('./pages/public/RefundPolicy'));

// Admin dashboard components
const AdminDashboardHome = lazy(
  () => import('./pages/admin-dashboard/AdminDashboardHome')
);
const AdminVerificationPage = lazy(
  () => import('./pages/admin-dashboard/AdminVerificationPage')
);
const AdminPaymentsPage = lazy(
  () => import('./pages/admin-dashboard/AdminPaymentsPage')
);
const AdminSubscriptionsPage = lazy(
  () => import('./pages/admin-dashboard/AdminSubscriptionsPage')
);
const AdminProSlotsPage = lazy(
  () => import('./pages/admin-dashboard/AdminProSlotsPage')
);
const AdminSubscriptionAnalyticsPage = lazy(
  () => import('./pages/admin-dashboard/AdminSubscriptionAnalyticsPage')
);
const AdminContractsPage = lazy(
  () => import('./pages/admin-dashboard/AdminContractsPage')
);
const AdminDisputesPage = lazy(
  () => import('./pages/admin-dashboard/AdminDisputesPage')
);
const AdminMessagesPage = lazy(
  () => import('./pages/admin-dashboard/AdminDirectMessages')
);
const AdminUsersPage = lazy(
  () => import('./pages/admin-dashboard/AdminUsersPage')
);
const AdminJobsPage = lazy(
  () => import('./pages/admin-dashboard/AdminJobsPage')
);
const AdminEditJobPage = lazy(
  () => import('./pages/admin-dashboard/AdminEditJobPage')
);
const AdminDraftManagement = lazy(
  () => import('./pages/admin-dashboard/AdminDraftManagementV2')
);
const TestDraftsTable = lazy(
  () => import('./pages/admin-dashboard/TestDraftsTable')
);
const AdminFeedbackManagementPage = lazy(
  () => import('./pages/admin-dashboard/feedback-management')
);
const AdminSocialMediaPosts = lazy(
  () => import('./pages/admin-dashboard/AdminSocialMediaPosts')
);
const AdminTrackingPage = lazy(
  () => import('./pages/admin-dashboard/AdminTrackingPage')
);
const AdminLabelingPage = lazy(
  () => import('./pages/admin-dashboard/AdminLabelingPage')
);
const AdminInstagramImportPage = lazy(
  () => import('./pages/admin-dashboard/AdminInstagramImportPage')
);

// Dev tools (only loaded when feature flag enabled)
const RankerTuningPage = lazy(() => import('./pages/dev/RankerTuningPage'));

// QueryClient is created in main.tsx, removed duplicate

// Wrapper component to handle email triggers inside QueryClientProvider
function AppWithProviders() {
  // Initialize email triggers - now inside QueryClientProvider context
  useAutoEmailTriggers();

  // Initialize real-time sync for ALL tables
  // This ensures UI instantly updates when database changes anywhere
  useRealtimeSync();

  // Check if site protection is enabled via environment variable
  const siteProtectionEnabled = import.meta.env.VITE_SITE_PROTECTION_ENABLED === 'true';

  const appContent = (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <HeroAnimationProvider>
            <SidebarStateProvider>
              <ChatPopupProvider>
                <TooltipProvider>
                  <QueryLoaderProvider>
                    <TopLoader />
                    <Sonner />
                    <TidioManager />
                    <BrowserNotificationsInit />
                    <NotificationPermissionBanner />
                    <Suspense fallback={<AnimatedLoadingScreen />}>
                <AnimatePresence mode="wait">
                  <Routes>
                {/* Public routes */}
                <Route path="/" element={<OAuthRedirectHandler><Index /></OAuthRedirectHandler>} />
                <Route path="/login" element={<Login />} />
                <Route path="/role-selection" element={<RoleSelection />} />
                <Route path="/filmmaker-type" element={<FilmmakerType />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/join-priority" element={<JoinPriority />} />
                <Route path="/priority/welcome" element={<PriorityWelcome />} />
                <Route path="/priority/set-password" element={<PrioritySetPassword />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/auth/callback" element={<GoogleCallback />} />
                <Route path="/auth/apple-callback" element={<AppleCallback />} />
                <Route path="/calendar-invite/:token" element={<CalendarInviteResponse />} />

                {/* Public browsing routes - NO AUTH REQUIRED */}
                <Route path="/talent" element={<PublicTalent />} />

                {/* Legal pages */}
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/delete-account" element={<DataDeletion />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/support" element={<Support />} />

                {/* Public portfolio routes */}
                <Route path="/portfolio/:username" element={<PublicPortfolio />} />
                <Route path="/booking/:linkCode" element={<PublicBookingPage />} />
                <Route path="/talent/portfolio/:id" element={<PublicPortfolioView />} />
                <Route path="/talent/:filmmakerId/calendar" element={<PublicCalendarPage />} />

                {/* Custom vanity URL for PRO filmmakers - must be after specific /talent/* routes */}
                <Route path="/talent/:customUrl" element={<CustomUrlPortfolio />} />

                {/* Job delegation claim route */}
                <Route path="/claim-job/:token" element={<ClaimJob />} />

                {/* Profile claim route for Instagram imports */}
                <Route path="/claim-profile/:token" element={<ClaimProfile />} />

                {/* App redirect for universal links */}
                <Route path="/app/jobs/:jobId" element={<JobRedirect />} />
                <Route path="/jobs/:jobId" element={<JobRedirect />} />

                {/* Verification route */}
                <Route path="/verify" element={<VerifyAccount />} />
                {/* Redirect legacy path to new path */}
                <Route
                  path="/verify-account"
                  element={<Navigate to="/verify" replace />}
                />

                {/* Feedback route */}
                <Route
                  path="/feedback"
                  element={
                    <ProtectedRoute
                      requireVerification={true}
                      requireProfileSetup={true}
                    >
                      <FeedbackPage />
                    </ProtectedRoute>
                  }
                />

                {/* Dual Account Setup - Combined filmmaker + client setup */}
                <Route
                  path="/dual-account-setup"
                  element={<DualAccountSetup />}
                />

                {/* Filmmaker Profile Setup Routes - Note these are NOT wrapped in ProtectedRoute */}
                <Route
                  path="/filmmaker/profile-setup/basic"
                  element={<ProfileSetupBasic />}
                />
                <Route
                  path="/filmmaker/profile-setup/professional"
                  element={<ProfileSetupProfessional />}
                />
                <Route
                  path="/filmmaker/profile-setup/portfolio"
                  element={<ProfileSetupPortfolio />}
                />
                <Route
                  path="/filmmaker/profile-setup/preview"
                  element={<ProfileSetupPreview />}
                />
                <Route
                  path="/filmmaker/verify-identity"
                  element={<VerifyIdentity />}
                />

                {/* Client Profile Setup Routes - Note these are NOT wrapped in ProtectedRoute */}
                <Route
                  path="/client/profile-setup/basic"
                  element={<ClientProfileSetupBasic />}
                />
                <Route
                  path="/client/profile-setup/preview"
                  element={<ClientProfileSetupPreview />}
                />
                <Route
                  path="/client/verify-identity"
                  element={<ClientVerifyIdentity />}
                />

                {/* Filmmaker Dashboard Routes - Consolidated 4-tab layout */}
                <Route
                  path="/filmmaker/dashboard"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <FilmmakerHomePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/profile"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <FilmmakerProfilePage />
                    </ProtectedRoute>
                  }
                />
                {/* Work Hub redirect to Jobs */}
                <Route
                  path="/filmmaker/dashboard/opportunities"
                  element={<Navigate to="/filmmaker/dashboard/jobs" replace />}
                />
                {/* Legacy business route redirects to pitching room */}
                <Route
                  path="/filmmaker/dashboard/business"
                  element={<Navigate to="/filmmaker/dashboard/pitching-room" replace />}
                />
                <Route
                  path="/filmmaker/dashboard/jobs"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <FilmmakerJobsPage />
                    </ProtectedRoute>
                  }
                />
                {/* Redirect old job details route to opportunities page with job query param */}
                <Route
                  path="/filmmaker/dashboard/jobs/:jobId"
                  element={<JobToOpportunitiesRedirect />}
                />
                <Route
                  path="/filmmaker/dashboard/jobs/:jobId/apply"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <JobApplication />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/jobs/:jobId/application-submitted"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <ApplicationSubmitted />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/applications"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <DashboardApplications />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/applications/:id"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <ApplicationDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/contract/:bidId"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <ContractDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/bookings/:bookingId"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <BookingDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/bookings"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <FilmmakerBookingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/bookings/requests/:bookingId"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <BookingDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/messages"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <FilmmakerMessagesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/earnings"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <DashboardEarnings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/account"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <FilmmakerAccountPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/subscribe"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <FilmmakerDashboardSubscribe />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/reviews"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <Reviews />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/payment-receipt/:bookingId"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <PaymentReceiptPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/receipts/:receiptId"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <PaymentReceipt />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/documents"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <DocumentsPage />
                    </ProtectedRoute>
                  }
                />
                {/* Filmmaker Pitching Room Route */}
                <Route
                  path="/filmmaker/dashboard/pitching-room"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <FilmmakerPitchingRoom />
                    </ProtectedRoute>
                  }
                />
                {/* Filmmaker Calendar Route */}
                <Route
                  path="/filmmaker/dashboard/calendar"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <FilmmakerCalendarPage />
                    </ProtectedRoute>
                  }
                />

                {/* Dual: Unified Invoices (Sent/Received tab toggle) */}
                <Route
                  path="/dashboard/invoices"
                  element={
                    <ProtectedRoute userType={['both']}>
                      <UnifiedInvoicesPage />
                    </ProtectedRoute>
                  }
                />

                {/* Filmmaker Invoice Routes */}
                <Route
                  path="/filmmaker/dashboard/invoices"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <FilmmakerInvoicesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/invoices/create"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <FilmmakerInvoiceCreatePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/invoices/:invoiceId"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <FilmmakerInvoiceDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/filmmaker/dashboard/invoices/:invoiceId/edit"
                  element={
                    <ProtectedRoute userType={['filmmaker', 'both']}>
                      <FilmmakerInvoiceCreatePage />
                    </ProtectedRoute>
                  }
                />

                {/* Client Dashboard Routes */}
                <Route
                  path="/client/dashboard"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ClientDashboardHome />
                    </ProtectedRoute>
                  }
                />
                {/* Legacy project list redirects to hiring tab */}
                <Route
                  path="/client/dashboard/projects"
                  element={<Navigate to="/client/dashboard/hiring?tab=projects" replace />}
                />
                <Route
                  path="/client/dashboard/projects/create"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <CreateProjectPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/projects/:projectId"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ProjectDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/projects/:projectId/edit"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <EditProjectPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/projects/:projectId/add-job"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <AddJobPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/projects/:projectId/jobs/:jobId"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <JobDetailsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/projects/:projectId/jobs/:jobId/edit"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <EditJobPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/projects/:projectId/jobs/:jobId/contract"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ContractPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/projects/:projectId/jobs/:jobId/payment"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <PaymentCheckoutPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/projects/:projectId/jobs/:jobId/payment-success"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <PaymentSuccessPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/projects/:projectId/jobs/:jobId/review"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ReviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/projects/:projectId/jobs/:jobId/dispute"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <DisputePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/receipts/:receiptId"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ClientReceiptPage />
                    </ProtectedRoute>
                  }
                />
                {/* New consolidated routes */}
                <Route
                  path="/client/dashboard/messages"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ClientMessagesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/find-talent"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ClientFindTalentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/hiring"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ClientHiringPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/pitching-room"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ClientBusinessPage />
                    </ProtectedRoute>
                  }
                />

                {/* Legacy route redirects */}
                <Route
                  path="/client/dashboard/talent"
                  element={<Navigate to="/client/dashboard/find-talent" replace />}
                />
                <Route
                  path="/client/dashboard/business"
                  element={<Navigate to="/client/dashboard/pitching-room" replace />}
                />
                <Route
                  path="/client/dashboard/expenditure"
                  element={<Navigate to="/client/dashboard/hiring?view=wallet" replace />}
                />
                <Route
                  path="/client/dashboard/invoices"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ClientInvoicesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/invoices/create"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ClientInvoiceCreatePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/invoices/:invoiceId"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ClientInvoiceDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/invoices/:invoiceId/edit"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ClientInvoiceCreatePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/account"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ClientDashboardAccount />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/booking/:linkCode"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ClientBookingFromLinkPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/calendar"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ClientCalendarPage />
                    </ProtectedRoute>
                  }
                />
                {CANVAS_ENABLED && (
                  <Route
                    path="/client/dashboard/canvas"
                    element={
                      <ProtectedRoute userType={['client', 'both']}>
                        <ProjectCanvasPage />
                      </ProtectedRoute>
                    }
                  />
                )}
                <Route
                  path="/client/dashboard/subscribe"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <ClientDashboardSubscribe />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/wallet/deposit/success"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <WalletDepositSuccessPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/wallet/deposit/failed"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <WalletDepositFailurePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/talent/:talentId"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <TalentDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client/dashboard/projects/:projectId/jobs/:jobId/applicants/:talentId"
                  element={
                    <ProtectedRoute userType={['client', 'both']}>
                      <JobApplicantProfile />
                    </ProtectedRoute>
                  }
                />

                {/* Admin Dashboard Routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="dashboard">
                      <AdminDashboardHome />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/verify"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="verify">
                      <AdminVerificationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/payments"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="payments">
                      <AdminPaymentsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/subscriptions"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="subscriptions">
                      <AdminSubscriptionsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/pro-slots"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="subscriptions">
                      <AdminProSlotsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/subscription-analytics"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="subscriptions">
                      <AdminSubscriptionAnalyticsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/contracts"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="contracts">
                      <AdminContractsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/disputes"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="disputes">
                      <AdminDisputesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/messages"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="messages">
                      <AdminMessagesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="users">
                      <AdminUsersPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/jobs"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="jobs">
                      <AdminJobsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/jobs/:jobId/edit"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="jobs">
                      <AdminEditJobPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/drafts"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="jobs">
                      <AdminDraftManagement />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/test-drafts"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="jobs">
                      <TestDraftsTable />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/feedback"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="feedback">
                      <AdminFeedbackManagementPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/social-media"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="social-media">
                      <AdminSocialMediaPosts />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/tracking"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="tracking">
                      <AdminTrackingPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/labeling"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="labeling">
                      <AdminLabelingPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/admin/instagram-import"
                  element={
                    <ProtectedRoute userType="admin" adminPermission="instagram-import">
                      <AdminInstagramImportPage />
                    </ProtectedRoute>
                  }
                />


                {/* Dev Tools - only available when enabled via env var */}
                {RANKER_TUNING_ENABLED && (
                  <Route
                    path="/dev/ranker-tuning"
                    element={<RankerTuningPage />}
                  />
                )}

                <Route path="*" element={<NotFound />} />
              </Routes>
                </AnimatePresence>
              </Suspense>
                  </QueryLoaderProvider>
                </TooltipProvider>
              </ChatPopupProvider>
            </SidebarStateProvider>
          </HeroAnimationProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );

  // Wrap with SiteProtection if enabled
  if (siteProtectionEnabled) {
    return <SiteProtection>{appContent}</SiteProtection>;
  }

  return appContent;
}

// Main App component - doesn't use hooks directly
function App() {
  return <AppWithProviders />;
}

export default App;
